import {ChangeDetectionStrategy, Component, computed, Inject, Injectable, Signal, signal} from "@angular/core";
import {toSignal} from "@angular/core/rxjs-interop";
import {MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef} from "@angular/material/dialog";
import {MatButtonModule} from "@angular/material/button";
import {MatIconModule} from "@angular/material/icon";
import {MatToolbarModule} from "@angular/material/toolbar";
import {TranslateModule} from "@ngx-translate/core";
import {Observable} from "rxjs";
import {map} from "rxjs/operators";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {erIkkeNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {asynkArbeidPågårSignal} from "felles-ng2/tjenester/AsynkArbeidPågårSignal";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {FormaterDurationPipe} from "felles-ng2/pipes/FormaterDurationPipe";
import {InlineMeldingKomponent} from "web-ng2/delt/komponenter/inline-melding/inline-melding";
import {TipsKomponent} from "web-ng2/delt/komponenter/tips/tips";
import {ProtokollDatatjeneste} from "felles-ng2/tjenester/datalager/ProtokollDatatjeneste";
import {MøteId, MøteModell, ProtokollTranskripsjonInformasjonModell} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {MøteTranskripsjon} from "../../tjenester/MøteTranskripsjon";
import {VisSpinnerDirektiv} from "web-ng2/delt/direktiver/button--visSpinner/button--visSpinner";
import {dialogRespons} from "felles-ng2/infrastruktur/material/matDialogHjelpere";
import {C3TodeltPaiKomponent, C3TodeltPaiKonfig} from "web-ng2/delt/komponenter/c3-todelt-pai/c3-todelt-pai";
import {farge_grå_400_skagastølstind, farge_profil_200_trondheim, farge_profil_500_bergen} from "felles-ng2/fargepalett";
import {MatTooltip} from "@angular/material/tooltip";
import {harTilgangTilProtokollforslag} from "felles-ng2/modell/transkripsjonModellHjelpere";

const debug = initDebugger('AvsluttMøteTranskripsjonDialogKomponent');


/**
 * Oppsummerer transkripsjonen av et møte rett etter at lydopptaket er stoppet.
 *
 * Serveren transkriberer lydopptakene fortløpende og asynkront, så et opptak som nettopp er lastet
 * opp er gjerne ikke transkribert ennå. Dialogen viser derfor hvor mange av lydopptakene som er
 * transkribert, og oppdaterer seg mens de øvrige blir ferdige – se {@link ProtokollDatatjeneste}.
 *
 * Bruker kan be om at forslaget til protokoll genereres når alle lydopptakene er transkribert, eller
 * fortsette opptaket dersom møtet likevel ikke er over. Selve forslaget vises ikke her, det hører til
 * på siden for redigering av protokollen.
 *
 * Bruker som er skjermet for noen av sakene i møtet får ikke generere forslaget, se {@link harTilgangTilProtokollforslag}.
 */
@Component({
    selector: 'avslutt-moete-transkripsjon-dialog',
    templateUrl: 'avslutt-møte-transkripsjon-dialog.html',
    styleUrls: ['avslutt-møte-transkripsjon-dialog.scss'],
    imports: [
        MatButtonModule,
        MatDialogModule,
        MatIconModule,
        MatToolbarModule,
        TranslateModule,
        TipsKomponent,
        InlineMeldingKomponent,
        FormaterDurationPipe,
        VisSpinnerDirektiv,
        C3TodeltPaiKomponent,
        MatTooltip
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AvsluttMøteTranskripsjonDialogKomponent {

    /** Viser dialogen og returnerer hva bruker gjorde, eller null dersom hen bare lukket den */
    static async vis(dialog: MatDialog, møte: MøteModell): Promise<AvsluttMøteTranskripsjonDialogRespons> {
        const dialogRef = dialog.open<AvsluttMøteTranskripsjonDialogKomponent, DialogParametre, AvsluttMøteTranskripsjonDialogRespons>(
          AvsluttMøteTranskripsjonDialogKomponent,
          {
              data: {møte},
              disableClose: true,
              ariaLabelledBy: 'avslutt-møte-transkripsjon-dialog-tittel',
              width: '600px'
          }
        );

        return await dialogRespons(dialogRef, null);
    }

    moete: MøteModell;
    /** Sann dersom bruker ikke er skjermet for noen av sakene i møtet, se {@link harTilgangTilProtokollforslag} */
    readonly harTilgangTilProtokollforslag: boolean;
    sisteFeil = signal<string | undefined>(undefined);

    /** Lydopptakene og transkripsjonene møtet har, null inntil protokolldetaljene er lastet */
    readonly transkripsjonsinformasjon: Signal<ProtokollTranskripsjonInformasjonModell | null>;

    /** Sann mens forslaget til protokoll genereres på server */
    genererer = asynkArbeidPågårSignal();
    /** Sann mens opptaket startes igjen */
    fortsetter = asynkArbeidPågårSignal();
    /** Sann mens en av handlingene i dialogen pågår, brukes for å låse knappene */
    readonly arbeiderPaagaar = computed(() => this.genererer() || this.fortsetter());

    /**
     * Sann når alle lydopptakene av møtet er transkribert eller feilet, og forslaget til protokoll dermed kan
     * genereres.
     */
    readonly alleOpptakTranskribertEllerFeilet = computed(() => {
        const informasjon = this.transkripsjonsinformasjon();
        return erIkkeNil(informasjon)
          && informasjon.antallOpptak > 0
          && informasjon.antallOpptak === informasjon.antallOpptakTranskribert + informasjon.antallOpptakTranskripsjonFeilet;
    });

    /** Sann når serveren alt har generert et forslag til protokoll fra lydopptakene */
    readonly protokollforslagTilgjengelig = computed(() => this.transkripsjonsinformasjon()?.oppsummertMoetereferatTilgjengelig ?? false);

    /** Sann når det er noe å generere, altså når alt er transkribert og forslaget ikke alt finnes, og bruker har tilgang til forslaget */
    readonly kanGenerereProtokollforslag = computed(() => this.harTilgangTilProtokollforslag && this.alleOpptakTranskribertEllerFeilet() && !this.protokollforslagTilgjengelig());

    readonly pieKonfig: Partial<C3TodeltPaiKonfig> = {
        ikkePåbegyntPieBorderFarge: farge_grå_400_skagastølstind,
        ikkePåbegyntFarge: farge_profil_200_trondheim, // transparent farge gir riktig hover-effekt inni md-button
        andelFarge: farge_profil_500_bergen,
        restFarge: farge_profil_200_trondheim,
        fullførtFarge: farge_profil_500_bergen
    }

    constructor(@Inject(MAT_DIALOG_DATA) parametre: DialogParametre,
                private dialogRef: MatDialogRef<AvsluttMøteTranskripsjonDialogKomponent, AvsluttMøteTranskripsjonDialogRespons>,
                private transkripsjon: MøteTranskripsjon,
                private avhengigheter: AvsluttMøteTranskripsjonDialogKomponentAvhengigheter,
                private oversettelser: Oversettelser
    ) {
        this.moete = parametre.møte;
        this.harTilgangTilProtokollforslag = harTilgangTilProtokollforslag(this.moete);
        this.transkripsjonsinformasjon = toSignal(this.avhengigheter.transkripsjonsinformasjon(this.moete.id), {initialValue: null});
    }

    async oppdaterInformasjon() {
        await this.avhengigheter.relastTranskripsjonsinformasjon(this.moete.id)
    }

    gaaTilRedigerProtokoll(){
        this.dialogRef.close('navigerTilRedigerProtokoll');
    }

    lukk(): void {
        this.dialogRef.close(null);
    }

    /**
     * Ber serveren generere forslaget til protokoll fra transkripsjonene, og lukker dialogen når det
     * er gjort. Forslaget vises ikke her – det er lagret på protokollen, og hentes fram av siden for
     * redigering av protokollen.
     */
    async genererProtokollforslag(): Promise<void> {
        this.sisteFeil.set(undefined);
        try {
            await this.genererer.utfør(() => this.avhengigheter.genererProtokollforslag(this.moete.id));

        } catch (feil: any) {
            debug('generering av forslag til protokoll feilet %o', feil);
            //feil fra selve serverkallet er alt meldt til bruker av ressursklienten
            this.sisteFeil.set(this.oversettelser.oversett(i18n('Kunne ikke generere forslag til protokoll, vennligst forsøk på nytt')));
        }
    }

    /**
     * Starter opptaket av møtet igjen, med samme mikrofon som sist.
     *
     * Opptaket ble stoppet da bruker klikket stopp, så dette er et nytt opptak – lydopptakene kommer
     * i tillegg til dem som alt er tatt opp, se {@link MøteTranskripsjon.start}.
     */
    async fortsettOpptaket(): Promise<void> {
        this.sisteFeil.set(undefined);
        try {
            await this.fortsetter.utfør(() => this.transkripsjon.start(this.moete, this.transkripsjon.sistBrukteMikrofon));
            this.dialogRef.close('fortsattOpptak');

        } catch (feil: any) {
            debug('fortsettelse av opptaket feilet %o', feil);
            this.sisteFeil.set(this.oversettelser.oversett(i18n('Kunne ikke starte lydopptaket igjen, vennligst forsøk på nytt')));
        }
    }
}

@Injectable()
export class AvsluttMøteTranskripsjonDialogKomponentAvhengigheter {

    constructor(private protokollDatatjeneste: ProtokollDatatjeneste) {

    }

    /**
     * Lydopptakene og transkripsjonene møtet har, holdt i takt med datalageret.
     *
     * Datalageret relaster protokollen hver gang serveren melder at et lydopptak er transkribert, så
     * antallet stiger av seg selv mens dialogen står åpen.
     */
    transkripsjonsinformasjon(møteId: MøteId): Observable<ProtokollTranskripsjonInformasjonModell> {
        return this.protokollDatatjeneste.hent(møteId).pipe(map(protokoll => protokoll.transkripsjonInformasjon));
    }

    /** Sørger for å oppdatere transkripsjonsinformasjonen til et møte, vil føre til at
     * oppdaterte verdi blir levert via observable returnert av kall til {@link #transkripsjonsinformasjon}  */
    relastTranskripsjonsinformasjon(møteId: MøteId): Promise<void> {
        return this.protokollDatatjeneste.relast(møteId);
    }

    /**
     * Ber serveren oppsummere transkripsjonene til et forslag til protokoll.
     *
     * Oppsummeringen er et llm-kall som koster tid og penger, men serveren lagrer resultatet på
     * protokollen slik at det bare genereres én gang – vi kaster derfor svaret her, og lar siden for
     * redigering av protokollen hente det lagrede forslaget.
     */
    async genererProtokollforslag(møteId: MøteId): Promise<void> {
        await this.protokollDatatjeneste.oppsummerMøtebehandlingsopptak(møteId);
    }
}

/**
 * Hva bruker gjorde i dialogen, eller null dersom hen bare lukket den.
 *
 * Merk at handlingene er utført når dialogen lukkes – responsen er kun til opplysning for den som
 * viste dialogen.
 */
export type AvsluttMøteTranskripsjonDialogRespons = 'fortsattOpptak' | 'navigerTilRedigerProtokoll' | null;

export interface DialogParametre {
    møte: MøteModell;
}

